import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Tag from "@/components/base/Tag";
import NewsCard from "@/components/feature/NewsCard";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { news } from "@/mocks/news";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function EpisodeDetail() {
  const { id } = useParams();
  const episode = episodes.find((e) => e.id === id);

  const meta = episode
    ? {
        title: `第${episode.season}季第${episode.episodeNumber}集 ${episode.title}剧情详情 - 美国禁忌4`,
        description: episode.summary,
      }
    : { title: "剧集详情 - 美国禁忌4", description: "美国禁忌4剧集章节详情。" };
  useDocumentMeta(meta);

  if (!episode) {
    return (
      <div className="px-4 md:px-10 py-16 text-center">
        <h1 className="font-heading text-2xl text-foreground-950">未找到该剧集章节</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的章节可能已被移除或链接有误。</p>
        <Link
          to="/episodes"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回剧集列表
        </Link>
      </div>
    );
  }

  const seasonEps = episodes.filter((e) => e.season === episode.season);
  const orderIndex = seasonEps.findIndex((e) => e.id === episode.id);
  const prev = orderIndex > 0 ? seasonEps[orderIndex - 1] : null;
  const next = orderIndex < seasonEps.length - 1 ? seasonEps[orderIndex + 1] : null;

  const castList = episode.cast
    .map((cid) => characters.find((c) => c.id === cid))
    .filter((c): c is (typeof characters)[number] => Boolean(c));

  const relatedNews = news
    .map((item) => ({
      item,
      score: item.tags.filter((t) => episode.tags.includes(t)).length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((r) => r.item);

  return (
    <div className="pb-6">
      {/* Hero */}
      <div className="relative w-full h-[320px] md:h-[440px] overflow-hidden">
        <img
          src={episode.cover}
          alt={`${episode.title} 剧照`}
          title={`${episode.title} - 美国禁忌4 第 ${episode.season} 季第 ${episode.episodeNumber} 集`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/10" />
        <div className="relative z-10 h-full flex items-end px-4 md:px-10 pb-6 md:pb-10">
          <div className="w-full">
            <Tag tone="accent">
              第 {episode.season} 季 · 第 {episode.episodeNumber} 集
            </Tag>
            <h1 className="mt-3 font-heading text-3xl md:text-5xl font-semibold tracking-wide text-foreground-950">
              {episode.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line text-accent-400" />
                首播 {episode.airDate}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line text-accent-400" />
                {episode.runtime}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-star-fill text-accent-400" />
                评分 {episode.rating}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-10">
        <div className="py-5 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "剧集章节", to: "/episodes" },
              { label: `第 ${episode.season} 季`, to: `/episodes?season=${episode.season}` },
              { label: `第 ${episode.episodeNumber} 集` },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-8 md:gap-10 mt-8">
          {/* Main column */}
          <div>
            <section>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-3">剧情概述</h2>
              <p className="text-[15px] leading-8 text-foreground-700">{episode.summary}</p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-3">关键剧情解析</h2>
              <p className="text-[15px] leading-8 text-foreground-700">{episode.analysis}</p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">主要出演角色</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                {castList.map((c) => (
                  <Link
                    key={c.id}
                    to={`/characters/${c.id}`}
                    className="group flex flex-col items-center text-center rounded-lg bg-background-100 border border-background-200 hover:border-accent-500/40 transition-colors p-4 cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-full overflow-hidden mb-3">
                      <img
                        src={c.avatar}
                        alt={`${c.name} 角色形象`}
                        title={`${c.name} - 美国禁忌4 角色档案`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <p className="font-heading text-sm text-foreground-950 group-hover:text-accent-400 transition-colors">
                      {c.name}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">饰 {c.actor}</p>
                  </Link>
                ))}
              </div>
            </section>

            {/* Prev / next */}
            <section className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {prev ? (
                <Link
                  to={`/episodes/${prev.id}`}
                  className="group flex items-center gap-3 rounded-lg bg-background-100 border border-background-200 hover:border-accent-500/40 transition-colors p-4 cursor-pointer"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-700 group-hover:text-accent-400 transition-colors">
                    <i className="ri-arrow-left-line" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500 font-label">上一集 · E{prev.episodeNumber}</span>
                    <span className="block text-sm text-foreground-900 truncate">{prev.title}</span>
                  </span>
                </Link>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 p-4 text-xs text-foreground-600 flex items-center justify-center">
                  已是本季第一集
                </div>
              )}
              {next ? (
                <Link
                  to={`/episodes/${next.id}`}
                  className="group flex items-center justify-end gap-3 rounded-lg bg-background-100 border border-background-200 hover:border-accent-500/40 transition-colors p-4 cursor-pointer text-right"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500 font-label">下一集 · E{next.episodeNumber}</span>
                    <span className="block text-sm text-foreground-900 truncate">{next.title}</span>
                  </span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-700 group-hover:text-accent-400 transition-colors">
                    <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 p-4 text-xs text-foreground-600 flex items-center justify-center">
                  已是本季最后一集
                </div>
              )}
            </section>
          </div>

          {/* Side column */}
          <aside className="flex flex-col gap-6">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base text-foreground-950 tracking-wide mb-3">本集标签</h2>
              <div className="flex flex-wrap gap-2">
                {episode.tags.map((t) => (
                  <Tag key={t} tone="secondary">
                    {t}
                  </Tag>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-background-200 space-y-2 text-sm text-foreground-600">
                <p className="flex items-center justify-between">
                  <span>所属季数</span>
                  <span className="text-foreground-900">第 {episode.season} 季</span>
                </p>
                <p className="flex items-center justify-between">
                  <span>集数</span>
                  <span className="text-foreground-900">第 {episode.episodeNumber} 集</span>
                </p>
                <p className="flex items-center justify-between">
                  <span>本季集数</span>
                  <span className="text-foreground-900">{seasonEps.length} 集</span>
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base text-foreground-950 tracking-wide mb-4">本季章节</h2>
              <ul className="space-y-1">
                {seasonEps.map((e) => (
                  <li key={e.id}>
                    <Link
                      to={`/episodes/${e.id}`}
                      className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors cursor-pointer ${
                        e.id === episode.id
                          ? "bg-accent-500/12 text-accent-400 border border-accent-500/25"
                          : "text-foreground-600 hover:bg-background-200/60 hover:text-foreground-900 border border-transparent"
                      }`}
                    >
                      <span className="font-label text-xs w-6 shrink-0">E{e.episodeNumber}</span>
                      <span className="truncate">{e.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* Related news */}
        <section className="mt-12 md:mt-16">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-6 h-[2px] bg-accent-500 inline-block" />
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide">相关资讯推荐</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            {relatedNews.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}